/*
 * (c) Copyright, 2024, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useContext, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import ActionsMenu from '../../components/shared/ActionsMenu'
import { useServiceContext } from '../../root'
import { useMarkNotificationsAsViewed, useMarkNotificationsAsUnviewed } from '../../hooks/useNotifications'
import ReadNotification from './read/ReadNotification'
import DeleteNotification from './DeleteNotification'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { TableMultiSelectionContext } from '../../components/rcm-table/TableMultiSelection'
import { useModal } from '~/contexts/ModalContext'

interface MenuOptions {
  selectedIds: string[]
  selectedWholeResult?: boolean
}

interface NotificationItem {
  id: string | number
  seen: boolean
  [key: string]: any
}

interface TableMenuProps {
  item: NotificationItem
  options?: MenuOptions
}

interface MenuItem {
  value: string
  action: () => void
  disabled?: boolean
}

const TableMenu: React.FC<TableMenuProps> = ({ item, options }) => {
  const context = useServiceContext()
  const queryClient = useQueryClient()
  const tableData = useRcmTableData()
  const multiSelectionContext = useContext(TableMultiSelectionContext)
  const [isOpen, setIsOpen] = useState(false)
  const { showModal } = useModal()
  const [items, setItems] = useState<MenuItem[]>([
    {
      value: 'LOADING...',
      action: () => {},
      disabled: true
    }
  ])
  const [showReadModal, setShowReadModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const isWholeResultSelection = options?.selectedWholeResult

  const { mutate: markAsViewed } = useMarkNotificationsAsViewed()
  const { mutate: markAsUnviewed } = useMarkNotificationsAsUnviewed()

  const emitNotificationsUpdate = () => {
    // Clear selection state (this will also clear the cached filter)
    multiSelectionContext?.deselectAll()
    // Refresh the table data directly
    tableData.refreshTable()
  }

  const setOptions = () => {
    const menu: MenuItem[] = []

    // ACTION_READ - Only for single selection
    if ((options?.selectedIds.length ?? 0) <= 1 && !isWholeResultSelection) {
      menu.push({
        value: 'ACTION_READ',
        action: () => {
          showModal(
            <ReadNotification
              notification={item as any}
              onClose={() => {
                setShowReadModal(false)
                emitNotificationsUpdate()
              }}
            />
          )
          setShowReadModal(true)
        }
      })
    }

    // ACTION_DELETE - Always available
    menu.push({
      value: 'ACTION_DELETE',
      action: () => {
        showModal(
          <DeleteNotification
            notificationIds={
              (options?.selectedIds.length ?? 0) > 1
                ? (options?.selectedIds.map((id) => (typeof id === 'string' ? parseInt(id, 10) : id)) ?? [])
                : typeof item.id === 'string'
                  ? parseInt(item.id, 10)
                  : (item.id as number)
            }
            isWholeResultSelection={isWholeResultSelection}
            notification={item as any}
            filter={(context.store.formRef as any)?.values?.filter}
            onClose={() => {
              setShowDeleteModal(false)
              emitNotificationsUpdate()
            }}
          />
        )
        setShowDeleteModal(true)
      }
    })

    // MARK_READ - Show if whole result OR unread single OR multiple selection
    if (
      isWholeResultSelection ||
      (!item.seen && (options?.selectedIds.length ?? 0) <= 1) ||
      (options?.selectedIds.length ?? 0) > 1
    ) {
      menu.push({
        value: 'MARK_READ',
        action: () => {
          const filter = (context.store.formRef as any)?.values?.filter
          // Use "All" endpoint when whole result is selected
          if (isWholeResultSelection) {
            markAsViewed(
              {
                isWholeResultSelection: true,
                filter: filter || {}
              },
              {
                onSuccess: () => {
                  emitNotificationsUpdate()
                }
              }
            )
          } else {
            // Use regular endpoint with messageIds for selected items
            const tNotificationId =
              (options?.selectedIds.length ?? 0) > 1
                ? options?.selectedIds.map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
                : typeof item.id === 'string'
                  ? parseInt(item.id, 10)
                  : item.id

            markAsViewed(
              { notificationIds: tNotificationId },
              {
                onSuccess: () => {
                  emitNotificationsUpdate()
                }
              }
            )
          }
        }
      })
    }

    // MARK_UNREAD - Show if whole result OR read single OR multiple selection
    if (
      isWholeResultSelection ||
      (item.seen && (options?.selectedIds.length ?? 0) <= 1) ||
      (options?.selectedIds.length ?? 0) > 1
    ) {
      menu.push({
        value: 'MARK_UNREAD',
        action: () => {
          const filter = (context.store.formRef as any)?.values?.filter
          // Use "All" endpoint when whole result is selected
          if (isWholeResultSelection) {
            markAsUnviewed(
              {
                isWholeResultSelection: true,
                filter: filter || {}
              },
              {
                onSuccess: () => {
                  emitNotificationsUpdate()
                }
              }
            )
          } else {
            // Use regular endpoint with messageIds for selected items
            const tNotificationId =
              (options?.selectedIds.length ?? 0) > 1
                ? options?.selectedIds.map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
                : typeof item.id === 'string'
                  ? parseInt(item.id, 10)
                  : item.id

            markAsUnviewed(
              { notificationIds: tNotificationId },
              {
                onSuccess: () => {
                  emitNotificationsUpdate()
                }
              }
            )
          }
        }
      })
    }

    setItems(menu)
  }

  useEffect(() => {
    setOptions()
  }, [isOpen])

  return (
    <ActionsMenu
      menuId={`action-menu-notifications-${item.id}`}
      onToggle={(open) => {
        setIsOpen(open)
      }}
      items={items}
    />
  )
}

export default TableMenu
